import { ReactNode, Dispatch, SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faWifi } from '@fortawesome/free-solid-svg-icons';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { AnimatePresence, motion } from 'framer-motion';
import { LinearProgress } from '@mui/material';
import { useSnackbar } from 'notistack';

import './DataTable.css';
import DataTableHead, { TableHeadCell } from "./DataTableHead";
import { useOnlineStatus } from "../Hooks/useOnlineStatus";
import { useLatestRef } from "../Hooks/useLatestRef";
import { Order } from "../../types/Order";
import { TableRowElement } from "./TableRowElement";
import { FilterConfig, TableFiltersElement } from './TableFiltersElement';
import { detectClobberedDraftRows } from './draftReconciliation';
import { VirtualizedRows } from './VirtualizedRows';
import { sortTableRows } from './sortTableRows';
import { withPulsedRow } from './withPulsedRow';

export type TableRow<IDType = number> = {
  id: IDType
  values: TableValue<IDType>[]
  rowTailwindClasses?: string
  rowContainerTailwindClasses?: string
}

export type TableOption = {
  value: string
  label: string
}

export type TableCellRenderHelpers = {
  /** The path built-in editable cells take on input: stages a draft for this cell, calls its
   *  onChange, and schedules its onChangeDebounced. */
  change: (value: any) => void;
  tabLock: {
    isTabLocked: boolean;
    registerInputRef: (el: HTMLElement | null) => void;
    handleLockedTabbing: (e: React.KeyboardEvent) => void;
  };
  row: {
    isSaving: boolean;
    setIsSaving: (saving: boolean) => void;
  };
  /**
   * Row-local "draft" overlay for transient cell values during an edit (e.g. typing,
   * picking from an Autocomplete in a custom render). Same draft state the built-in
   * editable inputs use, exposed here so custom renders can opt in.
   *
   * - `get()` — current display value for THIS cell: draft if set, otherwise v.value.
   *   Pass a name to read another cell's draft/value.
   * - `set(value)` — write a draft for THIS cell. Pass a name to update another cell
   *   (cross-cell cascade, e.g. Type → Scope).
   * - `clear()` — drop the draft for THIS cell (or a named one).
   *
   * Drafts auto-clear when a debounced/blur save completes via the cell's
   * onChangeDebounced / onBlur lifecycle.
   */
  draft: {
    get: (cellName?: string) => any;
    set: (value: any, cellName?: string) => void;
    clear: (cellName?: string) => void;
  };
};

export type TableValue<IDType = number> = {
  name: string
  type: "string" | "integer" | "decimal" | "date" | "boolean" | "select" | "percentage" | "custom" | "email"
  value: any
  editable?: boolean /** Used in combination with onChange to allow for changing row values directly */
  disabled?: boolean
  sortable?: boolean
  tailwindClasses?: string
  options?: TableOption[]
  tabLockKey?: string;
  onChange?: (row: TableRow<IDType>, newValue: any) => void; /** Used in combination with editable to allow for changing row values directly, interchangeable with onBlur */
  onBlur?: (row: TableRow<IDType>, newValue: any) => void; /** Used in combination with editable to allow for changing row values directly, interchangeable with onChange */
  onChangeDebounced?: (row: TableRow<IDType>, newValue: any) => void; /** Like onChange but fires after the user stops typing. Use instead of onBlur when you want auto-save while typing without an AJAX call on every keystroke */
  onChangeDebouncedMs?: number; /** Debounce delay in ms, defaults to 600 */
  render?: (
    tableValue: TableValue<IDType>,
    row: TableRow<IDType>,
    helpers: TableCellRenderHelpers
  ) => React.ReactNode;
}

interface DataTableProps<IDType = number> {
  data: TableRow<IDType>[];
  isLoading: boolean;
  isBackgroundFetching: boolean;
  error: string | null;
  pageNumber?: number;
  setPage?: (newPage: number) => void;
  pageSize?: number;
  totalCount?: number;
  sortField?: string;
  sortDirection: Order;
  totalPages?: number;
  tableFilters?: FilterConfig[];
  setTableFilters?: Dispatch<SetStateAction<FilterConfig[]>>;
  additionalTableFilterSectionContent?: ReactNode;
  insertRowRenderer?: () => ReactNode;
  expandedRowRenderer?: (row: TableRow<IDType>) => ReactNode;
  minTableWidthPixels?: number; // Note: Minimum width of the table, before the horizontal scrollbar appears
  maxTableWidthPixels?: number;
  minTableHeightCssValue?: string;
  maxTableHeightCssValue?: string; // Note: Maximum height of the table, beyond which the scrollbar appears, as a CSS value (eg. '1000px' or 'calc(100vh - 24rem)')
  /** When true, the scroll box fills the remaining flex height of the page instead of using
   *  min/maxTableHeightCssValue. A flex-column ancestor that bounds the height (e.g. <TablePage>)
   *  is required. The box never shrinks below minTableFloorCssValue; past that the page scrolls.
   *  Ignored for nested tables. */
  fillHeight?: boolean;
  /** Floor height for fillHeight mode, below which the page scrolls rather than the table
   *  collapsing to nothing on short viewports. Defaults to '15rem'. */
  minTableFloorCssValue?: string;
  disableContainer?: boolean; // Note: Disable container width limit to be able to fill the whole screen
  isNestedTable?: boolean;
  overrideHeaderCells?: TableHeadCell[];
  singleEntryMode?: boolean;
  onExitSingleEntryMode?: () => void;
  tableHeaderContent?: ReactNode;
  tableFooterContent?: ReactNode;
  noResultsContent?: ReactNode | (() => ReactNode);
  enableTabLocking?: boolean;
  lockedTabKey?: string | null;
  setLockedTabKey?: (key: string | null) => void;
  /** When set, the row list switches to a windowed (virtualized) render once
   *  `sortedData.length` exceeds this number. Use on pages that show many rows
   *  without pagination (e.g. RoyaltySplits). Off by default. */
  virtualizeOver?: number;
  /** Initial row-height estimate (px) for the virtualizer. Rows are measured
   *  on mount, so this only affects the first paint and the size of the scroll
   *  area before any row has been rendered. Defaults to 56. */
  estimatedRowHeightPx?: number;
  /** When true, measure the scroll box and report (via onAutoPageSize) how many rows fit, so a
   *  clientPaginate table fills the visible area without leaving a gap or forcing scroll. Uses
   *  estimatedRowHeightPx as the row height, so set that to roughly match this table's rows. */
  autoPageSize?: boolean;
  onAutoPageSize?: (rows: number) => void;
  /** Row id to give the one-shot attention glow, as returned by useScrollAndPulseRow. */
  pulseRowKey?: string | number | null;
}

/* If details button is visible (nested columns added), the total width of the rest of the columns can be at most 95%
* Same applies to Add button, which takes 5% of available space
*
* When supplying render to the table values, keep in mind that the passed in parameters correspond to different values.
* The tableValue will be whatever was provided as the value to the given table row, whereas the tableRow will have
* all the values updated by the update display table hook. */
export const DataTable = <IDType extends number | string = number>(props: DataTableProps<IDType>) => {
  const {
    error,
    data,
    isLoading,
    isBackgroundFetching,
    pageNumber,
    setPage,
    pageSize,
    totalCount,
    sortField,
    sortDirection,
    totalPages,
    tableFilters,
    setTableFilters,
    additionalTableFilterSectionContent,
    insertRowRenderer,
    expandedRowRenderer,
    minTableWidthPixels = 720,
    maxTableWidthPixels,
    minTableHeightCssValue = '800px',
    maxTableHeightCssValue = '800px',
    fillHeight = false,
    minTableFloorCssValue = '15rem',
    disableContainer,
    isNestedTable = false,
    overrideHeaderCells,
    singleEntryMode,
    onExitSingleEntryMode,
    tableHeaderContent,
    tableFooterContent,
    noResultsContent,
    enableTabLocking,
    lockedTabKey,
    setLockedTabKey,
    virtualizeOver,
    estimatedRowHeightPx = 56,
    autoPageSize = false,
    onAutoPageSize,
    pulseRowKey,
  } = props;
  const [sortFieldState, setSortFieldState] = useState<string>('Refresh');
  const [sortDirectionState, setSortDirectionState] = useState<Order>('asc');
  // Note: only the current page is cached offline, so paging would fire a request that cannot succeed.
  const isOnline = useOnlineStatus();
  const showDetailsColumnHeadCell = expandedRowRenderer !== undefined;
  // Note: flex-fill is opt-in and never applies to nested tables (they manage their own height).
  const fill = fillHeight && !isNestedTable;

  const tabInputRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrolledPastTop, setScrolledPastTop] = useState(false);

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const onScroll = () => setScrolledPastTop(root.scrollTop > 0);
    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener('scroll', onScroll);
  }, []);

  const onAutoPageSizeRef = useLatestRef(onAutoPageSize);
  const lastAutoRowsRef = useRef(0);
  const hasInsertRow = !!insertRowRenderer;
  useLayoutEffect(() => {
    if (!autoPageSize) return;
    const root = scrollContainerRef.current;
    if (!root) return;
    const rowH = estimatedRowHeightPx > 0 ? estimatedRowHeightPx : 56;
    const compute = () => {
      const chrome = rowH * (1 + (hasInsertRow ? 1 : 0));
      const rows = Math.max(5, Math.floor((root.clientHeight - chrome) / rowH));
      if (rows !== lastAutoRowsRef.current) {
        lastAutoRowsRef.current = rows;
        onAutoPageSizeRef.current?.(rows);
      }
    };
    const observer = new ResizeObserver(compute);
    observer.observe(root);
    compute();
    return () => observer.disconnect();
  }, [autoPageSize, estimatedRowHeightPx, hasInsertRow, onAutoPageSizeRef]);

  const { enqueueSnackbar } = useSnackbar();
  const dirtyRowIdsRef = useRef<Set<IDType>>(new Set());
  const prevRowIdsRef = useRef<Set<IDType>>(new Set());

  const handleDraftStateChange = useCallback((rowId: IDType, hasDrafts: boolean) => {
    if (hasDrafts) dirtyRowIdsRef.current.add(rowId);
    else dirtyRowIdsRef.current.delete(rowId);
  }, []);

  useEffect(() => {
    const currentIds = new Set(data.map(r => r.id));

    if (!isBackgroundFetching) {
      const clobbered = detectClobberedDraftRows(prevRowIdsRef.current, currentIds, dirtyRowIdsRef.current);
      if (clobbered.length > 0) {
        enqueueSnackbar(
          clobbered.length === 1
            ? "A row you were editing was removed by a live update; your unsaved changes were discarded."
            : `${clobbered.length} rows you were editing were removed by a live update; your unsaved changes were discarded.`,
          { variant: "warning", autoHideDuration: 6000 }
        );
        clobbered.forEach(id => dirtyRowIdsRef.current.delete(id));
      }
    }

    for (const id of [...dirtyRowIdsRef.current]) {
      if (!currentIds.has(id)) dirtyRowIdsRef.current.delete(id);
    }
    prevRowIdsRef.current = currentIds;
  }, [data, isBackgroundFetching, enqueueSnackbar]);

  const headCells = useMemo(() => {
    return data[0]?.values.map((value) => ({
      name: value.name,
      sortable: value.sortable ?? false,
      tailwindClasses: value.tailwindClasses,
    })) ?? [];
  }, [data]);

  const sortedData = useMemo(
    () => sortTableRows(data, sortFieldState, sortDirectionState),
    [data, sortFieldState, sortDirectionState]
  );

  const sortClickHandler = useCallback((property: string, order: Order) => {
    setSortFieldState(property);
    setSortDirectionState(order);
  }, []);

  const pulsedData = withPulsedRow(sortedData, pulseRowKey);

  const shouldVirtualize = virtualizeOver !== undefined && sortedData.length > virtualizeOver;

  const sortedDataRef = useLatestRef(sortedData);

  const scrollToIndexRef = useRef<((index: number) => void) | null>(null);

  const registerTabInputRef = useCallback((rowId: IDType, tabLockKey: string, el: HTMLElement | null) => {
    tabInputRefs.current[`${String(rowId)}::${tabLockKey}`] = el;
  }, []);

  const focusNextLockedCell = useCallback((rowId: IDType, tabLockKey: string) => {
    const data = sortedDataRef.current;
    const currentIndex = data.findIndex(r => r.id === rowId);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + 1;
    if (nextIndex >= data.length) return;
    const nextRowId = data[nextIndex].id;

    const focusNow = () => {
      const nextEl = tabInputRefs.current[`${String(nextRowId)}::${tabLockKey}`];
      if (!nextEl) return;
      (nextEl as HTMLElement).focus?.();
      (nextEl as HTMLInputElement).select?.();
    };

    const scrollToIndex = scrollToIndexRef.current;
    if (scrollToIndex) {
      scrollToIndex(nextIndex);
      requestAnimationFrame(() => requestAnimationFrame(focusNow));
    } else {
      focusNow();
    }
  }, [sortedDataRef]);

  return (
    <>
      <div className={`flex flex-col flex-1 w-full ${fill ? 'min-h-0' : ''} ${isNestedTable ? 'border-b border-x border-theme-table-row-border px-4 pb-4 pt-2' : `${tableHeaderContent ? 'mt-0' : 'mt-4 compact:mt-1'} ${disableContainer ? '' : 'items-center'}`}`}>
        <div className={`flex flex-col flex-1 ${fill ? 'min-h-0' : ''} ${isNestedTable ? '' : 'container'} ${disableContainer ? 'max-w-full mx-auto w-full' : ''}`}>
          {/* Note: shrink-0 so the filters/header keep their natural height under fillHeight; only the scroll box flexes. */}
          {!!tableHeaderContent && <div className="shrink-0">{tableHeaderContent}</div>}
          {!singleEntryMode && tableFilters && tableFilters.length > 0 && setTableFilters &&
            <TableFiltersElement
              state={tableFilters}
              setState={setTableFilters}
              data={data}
              disableContainer={disableContainer}
              maxTableWidthPixels={maxTableWidthPixels}
              additionalTableFilterSectionContent={additionalTableFilterSectionContent}
            />
          }
          {error === null &&
            !singleEntryMode &&
            pageNumber !== undefined &&
            pageSize !== undefined &&
            totalCount !== undefined &&
            setPage &&
            <div className="shrink-0" style={{
              maxWidth: disableContainer ? maxTableWidthPixels : '',
              margin: '0 auto',
              width: '100%'
            }}>
              <Stack spacing={2} direction="row" className="my-2 compact:my-0 compact:mb-0.5 justify-end items-center">
                {!isOnline && (totalPages ?? Math.ceil(totalCount / pageSize)) > 1 && (
                  <span className="text-xs text-theme-text-muted mr-auto">
                    Offline: only saved pages are available
                  </span>
                )}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={totalPages ?? Math.ceil(totalCount / pageSize)}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                  >
                    <Pagination
                      count={totalPages ?? Math.ceil(totalCount / pageSize)}
                      page={pageNumber}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontWeight: 'bold',
                          color: 'var(--theme-text)'
                        },
                        '& .MuiPaginationItem-root.Mui-selected': {
                          backgroundColor: 'var(--theme-info)',
                          color: 'white',
                        },
                      }}
                    />
                  </motion.div>
                </AnimatePresence>
              </Stack>
            </div>
          }
          <div
            ref={scrollContainerRef}
            className={`flex-1 text-center ${isNestedTable ? 'h-full' : 'bg-theme-surface-2 border border-theme-table-border overflow-x-auto overflow-y-auto'} ${tableFooterContent ? '!mb-0 !border-b-0' : ''}`}
            style={{
              minHeight: fill ? minTableFloorCssValue : (isNestedTable ? 'min-content' : minTableHeightCssValue),
              maxHeight: fill ? undefined : maxTableHeightCssValue,
              width: disableContainer ? '100%' : 'unset'
            }}
          >
            <div className="table-container"
              style={{
                minWidth: `${(sortedData.length !== 0 || overrideHeaderCells) ? minTableWidthPixels + 'px' : 'auto'}`,
                maxWidth: `${maxTableWidthPixels ? (maxTableWidthPixels + 'px') : 'auto'}`,
              }}
            >
              <section className="pb-1">
                {error !== null ?
                  <div
                    className="line"
                    style={{
                      minHeight: fill ? minTableFloorCssValue : minTableHeightCssValue,
                      maxHeight: fill ? undefined : maxTableHeightCssValue,
                    }}
                  >
                    <div className="animate-fade-in h-full w-full flex flex-col items-center justify-center gap-3 px-6 py-10 text-center text-theme-text-muted">
                      <div className="flex items-center justify-center rounded-full border border-theme-border bg-theme-surface h-20 w-20">
                        <FontAwesomeIcon icon={isOnline ? faTriangleExclamation : faWifi} className={`text-3xl ${isOnline ? 'text-theme-warning' : 'text-theme-text-muted'}`} />
                      </div>
                      {isOnline ? (
                        <>
                          <p className="font-semibold text-theme-text text-xl">Something went wrong</p>
                          <p className="max-w-[28rem] text-sm">
                            We couldn't load this data. Please try again.
                            {error && <span className="mt-1 block break-words text-theme-text-muted/80">{error}</span>}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-theme-text text-xl">Not available offline</p>
                          <p className="max-w-[28rem] text-sm">
                            This page hasn't been saved for offline use. Reconnect to load it; pages you opened before going offline stay available.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  : (
                    <>
                      {singleEntryMode && onExitSingleEntryMode &&
                        <div
                          className="flex justify-between items-center my-4 p-2 bg-theme-warning border-l-4 border-theme-warning-muted text-white fade-up">
                          <span>Showing a single entry. Filters and pagination are disabled.</span>
                          <button
                            className="bg-theme-warning-muted hover:bg-theme-warning-muted-hover text-white px-4 py-1 rounded transition duration-300 ease-in-out"
                            onClick={onExitSingleEntryMode}
                          >
                            Exit Single Entry Mode
                          </button>
                        </div>
                      }

                      <DataTableHead
                        headCells={headCells}
                        showDetailsColumnHeadCell={showDetailsColumnHeadCell}
                        isLoading={isLoading}
                        isNestedTable={isNestedTable}
                        isEmpty={data.length === 0}
                        sortDirection={sortDirection}
                        sortField={sortField}
                        sortClickHandler={sortClickHandler}
                        overrideHeaderCells={overrideHeaderCells}
                        enableTabLocking={enableTabLocking}
                        lockedTabKey={lockedTabKey}
                        setLockedTabKey={setLockedTabKey}
                        scrolledPast={scrolledPastTop}
                      />
                      <div className="table-body relative">
                        {isLoading ? (
                          <>
                            {Array.from({ length: 8 }).map((_, i) => (
                              <div key={i} className="line border-b border-theme-table-border animate-fade-in">
                                <ul className="!justify-between px-3 py-3.5">
                                  <li className="basis-[16%] h-3.5 bg-theme-table-border/70 animate-pulse rounded-sm" />
                                  <li className="basis-[28%] h-3.5 bg-theme-table-border/70 animate-pulse rounded-sm" />
                                  <li className="basis-[20%] h-3.5 bg-theme-table-border/70 animate-pulse rounded-sm" />
                                  <li className="basis-[30%] h-3.5 bg-theme-table-border/70 animate-pulse rounded-sm" />
                                </ul>
                              </div>
                            ))}
                          </>
                        ) : (
                          <>
                            {!singleEntryMode && insertRowRenderer && (
                              <div className="line border-b border-theme-table-border animate-fade-in">
                                {insertRowRenderer()}
                              </div>
                            )}

                            {isBackgroundFetching && <div className="absolute top-0 w-full animate-fade-in z-20"><LinearProgress /></div>}
                            {!(sortedData.length === 0) ? (
                              shouldVirtualize ? (
                                <VirtualizedRows<IDType>
                                  rows={pulsedData}
                                  scrollContainerRef={scrollContainerRef}
                                  estimatedRowHeightPx={estimatedRowHeightPx}
                                  isBackgroundFetching={isBackgroundFetching}
                                  expandedRowRenderer={expandedRowRenderer}
                                  lockedTabKey={enableTabLocking ? lockedTabKey : null}
                                  registerTabInputRef={registerTabInputRef}
                                  focusNextLockedCell={focusNextLockedCell}
                                  onDraftStateChange={handleDraftStateChange}
                                  scrollToIndexRef={scrollToIndexRef}
                                />
                              ) : (
                                <div className={`transition-opacity ease-in-out ${isBackgroundFetching ? 'opacity-80 pointer-events-none' : ''}`}>
                                  {pulsedData.map((row) => (
                                    <TableRowElement<IDType>
                                      key={row.id}
                                      tableRow={row}
                                      isLoading={false}
                                      showDetailsColumnHeadCell={!!expandedRowRenderer}
                                      expandedRowRenderer={expandedRowRenderer}
                                      rowTailwindClasses={row.rowTailwindClasses}
                                      rowContainerTailwindClasses={row.rowContainerTailwindClasses}
                                      lockedTabKey={enableTabLocking ? lockedTabKey : null}
                                      registerTabInputRef={registerTabInputRef}
                                      focusNextLockedCell={focusNextLockedCell}
                                      onDraftStateChange={handleDraftStateChange}
                                    />
                                  ))}
                                </div>
                              )
                            ) : (
                              <div className="line border-b border-theme-table-border animate-fade-in">
                                <ul className="result">
                                  <li className="text-center py-3 flex items-center justify-center text-[0.9rem] w-full text-theme-table-row-contrast">
                                    {typeof noResultsContent === "function"
                                      ? noResultsContent()
                                      : (noResultsContent ?? "No results found.")}
                                  </li>
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )
                }
              </section>
            </div>
          </div>
          {tableFooterContent && error === null && <div className="shrink-0">{tableFooterContent}</div>}
        </div>
      </div>
    </>
  )
}